// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import {
  AssetTransitReportRow,
  PresenceReportResponse,
} from '@/report/api/types';
import { toPresenceBits } from '@/report/utilities/to-presence-bits';

const PATH = '/v1/report/asset/in-transit';

interface Parameters {
  start: string;
  end?: string;
}

interface Response {
  data: PresenceReportResponse<AssetTransitReportRow>;
}

// The base64 mask is dropped once decoded so each row holds one representation, not two.
export interface DecodedAssetTransitReportRow
  extends Omit<AssetTransitReportRow, 'presenceBitMask'> {
  presenceBits: Uint8Array;
}

/**
 * Requests a transit report of assets for a given time period.
 *
 * @param parameters.start - The start of the time period.
 * @param parameters.end - The end of the time period.
 */
export async function getAssetTransitReport(parameters: Parameters) {
  // This API response is expected to be large, so the response is not parsed
  // for performance reasons. Instead, the response type is assumed to be
  // correct and the data is returned directly.
  const { data } = await api.get<Response>(PATH, { params: parameters });

  // Decode once per row here, never inside a column accessor, which runs per row per column.
  const rows = data.data.data.map(({ presenceBitMask, ...row }) => ({
    ...row,
    presenceBits: toPresenceBits(presenceBitMask),
  }));

  return { startOn: data.data.startOn, dayCount: data.data.dayCount, rows };
}
